import React, { useRef, useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Divider,
} from "@mui/material";
import Logo from "../../imges/logos/mindsinaction/logo.png"; // Ensure correct path

// Company details
const companyDetails = {
  name: "Mindsinaction STEAM CENTER.",
  email: "admin@mindsinaction.com.na",
  cell: "+264 81 363 0529",
  address: "Toivo ya Toivo No.6. Suiderhof,Windhoek, Huster Machinetoolbuilding First Floor"
};

const Invoice = ({ userData, studentData, students, userType, modules }) => {
  const invoiceRef = useRef(null); // Reference to the invoice card
  const [hideButton, setHideButton] = useState(false); // State to toggle button visibility

  const generateInvoiceNumber = () => {
    return `INV-${Math.floor(100000 + Math.random() * 900000)}`;
  };

  const invoiceNumber = generateInvoiceNumber();
  const currentDate = new Date().toLocaleDateString();

  const handleDownloadPDF = async () => {
    try {
      // Hide the button before capturing
      setHideButton(true);

      // Wait for the state update to apply (ensure button is hidden)
      await new Promise((resolve) => setTimeout(resolve, 0));

      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2, // Increase resolution for better quality
        useCORS: true, // Allow cross-origin images (e.g., logo)
        backgroundColor: "#ffffff", // Ensure white background
      });

      const imgData = canvas.toDataURL("image/png");
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
      });

      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = doc.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;

      doc.addImage(imgData, "PNG", 0, 0, scaledWidth, scaledHeight);
      doc.save(`invoice_${invoiceNumber}.pdf`);

      // Show the button again after capturing
      setHideButton(false);
    } catch (error) {
      setHideButton(false); // Ensure button is shown even if an error occurs
      alert(`Error generating PDF: ${error.message}`);
    }
  };

  const modulename = modules.find(m=> m.id === studentData.course);

  return (
    <Card
      ref={invoiceRef}
      sx={{ mb: 4, maxWidth: '600px', minWidth: '300px', mx: {xs: 0, sm: 1, md: 2}, backgroundColor: "#ffffff" }}
    >
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <img src={Logo} alt="Company Logo" style={{ width: 150, height: "auto" }} />
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {companyDetails.name}
            </Typography>
            <Typography variant="subtitle2">Email: {companyDetails.email}</Typography>
            <Typography variant="subtitle2">Cell: {companyDetails.cell}</Typography>
            <Typography variant="subtitle2">{companyDetails.address}</Typography>
          </Box>
        </Box>
        <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
            <Typography variant="h5" gutterBottom>
                Invoice
            </Typography>
            <Box display={'flex'} gap={1}>
                <Typography>STATUS: </Typography>
                {studentData.dueToUs > studentData.dueToYou ? (
                    <Typography color="red">PENDING</Typography>
                ):(
                    <Typography color="green">PAID</Typography>
                )}
            </Box>
        </Box>
        
        <Divider sx={{ my: 1 }} />
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1">
            <strong>Invoice Number:</strong> {invoiceNumber}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Date:</strong> {currentDate}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Recipient:</strong>{" "}
            {userData?.firstName ||
              userData?.fullName?.split(" ")[0] || "Unknown"}{" "}
            {userData?.lastName ||
              userData?.fullName?.split(" ").slice(1).join(" ") || ""}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Email:</strong> {userData?.email || "N/A"}
          </Typography>
        </Box>

        <Typography variant="h6" gutterBottom>
          Invoice Details
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: "#3f51b5", color: "#ffffff" }}>
                Description
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: "#3f51b5", color: "#ffffff" }}
              >
                Amount (N$)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userType === "student" && studentData && (
              <TableRow>
                <TableCell>Enrolled Modules: {modulename.module_name || "None"}</TableCell>
                <TableCell align="right">
                  {studentData.dueToUs?.toFixed(2) || "0.00"}
                </TableCell>
              </TableRow>
            )}
            {userType === "parent" &&
              students.length > 0 &&
              students.map((student, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {student.firstName} {student.lastName} -{" "}
                    {student.course || "No modules enrolled"}
                  </TableCell>
                  <TableCell align="right">
                    {student.dueToUs?.toFixed(2) || "0.00"}
                  </TableCell>
                </TableRow>
              ))}
            {userType === "parent" && students.length === 0 && (
              <TableRow>
                <TableCell>No students registered</TableCell>
                <TableCell align="right">0.00</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell>
                <strong>Total</strong>
              </TableCell>
              <TableCell align="right">
                <strong>
                  {userType === "student"
                    ? studentData?.dueToUs?.toFixed(2) || "0.00"
                    : students
                        .reduce((sum, s) => sum + (s.dueToUs || 0), 0)
                        .toFixed(2)}
                </strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 2, ...(hideButton && { display: "none" }) }}
          onClick={handleDownloadPDF}
        >
          Download PDF
        </Button>
      </CardContent>
    </Card>
  );
};

export default Invoice;